import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Globe,
  GraduationCap,
  Heart,
  Menu,
  Sparkles,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import logo from '@/assets/logo.png';

const InitiativeHeader = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const t = (ar: string, en: string) => (isRTL ? ar : en);

  const navItems = [
    { path: '/initiative', label: { ar: 'المبادرة', en: 'Initiative' }, end: true },
    { path: '/initiative/coaches', label: { ar: 'تقدّم للمنحة', en: 'Apply' } },
    { path: '/initiative/sponsor', label: { ar: 'الرعاية', en: 'Sponsor' } },
  ];

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 18 }}
        className={cn(
          'fixed top-0 inset-x-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-[#070138]/90 backdrop-blur-xl border-b border-white/10 shadow-2xl shadow-black/30'
            : 'bg-[#070138]/55 backdrop-blur-md'
        )}
      >
        {/* Top strip — partner credit (separation enforced) */}
        <div className="border-b border-white/5">
          <div className="container mx-auto px-4">
            <div className="h-8 flex items-center justify-between text-[11px] uppercase tracking-wider text-white/65">
              <div className="hidden sm:flex items-center gap-3">
                <img
                  src={logo}
                  alt="Champions Academies"
                  className="h-5 w-auto"
                />
                <span className="text-white/30">×</span>
                <span className="font-bold text-white">
                  {t('نادي الجلاء', 'Al-Jalaa Club')}
                </span>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <span className="text-white/40">
                  {t('الداعم التقني', 'Technical Supporter')}:
                </span>
                <a
                  href="https://champions-hub.com"
                  target="_blank"
                  rel="noreferrer"
                  className="opacity-85 hover:opacity-100 transition-opacity"
                  aria-label="Champions Hub"
                >
                  <img
                    src="/logos/champions-hub-horizontal-dark.png"
                    alt="Champions Hub"
                    className="h-4 w-auto"
                  />
                </a>
              </div>
              <Link
                to="/"
                className="ml-auto md:ml-0 inline-flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
              >
                <ArrowIcon className="h-3 w-3" />
                <span>{t('الموقع الرئيسي', 'Main site')}</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Main bar */}
        <div className="container mx-auto px-4">
          <div className="h-16 lg:h-18 flex items-center justify-between gap-4">
            <Link to="/initiative" className="flex items-center gap-3 group">
              <div className="relative">
                <img src={logo} alt="" className="h-9 lg:h-10 w-auto" />
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FFA92C]/40 to-[#FFA92C]/0 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFA92C] font-bold">
                  {t('من العالم — إلى غزة', 'From the world — to Gaza')}
                </div>
                <div className="text-base lg:text-lg font-bold text-white">
                  {t('أبطال بلا حدود', 'Champions Without Borders')}
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'relative px-4 py-2 rounded-full text-sm font-semibold transition-colors',
                      isActive
                        ? 'text-[#070138] bg-[#FFA92C]'
                        : 'text-white/85 hover:text-white hover:bg-white/10'
                    )
                  }
                >
                  {t(item.label.ar, item.label.en)}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                onClick={toggleLanguage}
                aria-label={t('تبديل اللغة', 'Toggle language')}
                className="hidden sm:inline-flex h-10 w-10 rounded-full items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Globe className="h-5 w-5" />
              </button>

              <Link to="/initiative/coaches" className="hidden sm:block">
                <Button
                  size="sm"
                  className="bg-[#FFA92C] text-[#070138] hover:bg-[#FFA92C]/90 font-bold gap-1.5 h-10 px-4"
                >
                  <GraduationCap className="h-4 w-4" />
                  {t('تقدّم الآن', 'Apply now')}
                </Button>
              </Link>

              <button
                onClick={() => setMobileOpen(true)}
                aria-label={t('فتح القائمة', 'Open menu')}
                className="lg:hidden h-10 w-10 rounded-full inline-flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 lg:hidden"
            />
            <motion.div
              initial={{ x: isRTL ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: isRTL ? '100%' : '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={cn(
                'fixed top-0 bottom-0 w-80 max-w-[85vw] bg-[#070138] border-white/10 z-50 lg:hidden overflow-y-auto',
                isRTL ? 'right-0 border-l' : 'left-0 border-r'
              )}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <img src={logo} alt="" className="h-9 w-auto" />
                    <div className="leading-tight">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFA92C] font-bold">
                        {t('من العالم — إلى غزة', 'From the world — to Gaza')}
                      </div>
                      <div className="text-sm font-bold text-white">
                        {t('أبطال بلا حدود', 'Champions Without Borders')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="h-10 w-10 rounded-full inline-flex items-center justify-center text-white hover:bg-white/10"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <nav className="space-y-2 mb-8">
                  {navItems.map((item, idx) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <NavLink
                        to={item.path}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            'block px-4 py-3 rounded-xl font-semibold transition-colors',
                            isActive
                              ? 'bg-[#FFA92C] text-[#070138]'
                              : 'text-white/85 hover:bg-white/10'
                          )
                        }
                      >
                        {t(item.label.ar, item.label.en)}
                      </NavLink>
                    </motion.div>
                  ))}
                </nav>

                <div className="space-y-2">
                  <Link to="/initiative/coaches" className="block">
                    <Button className="w-full bg-[#FFA92C] text-[#070138] hover:bg-[#FFA92C]/90 font-bold gap-2">
                      <GraduationCap className="h-4 w-4" />
                      {t('تقدّم الآن', 'Apply now')}
                    </Button>
                  </Link>
                  <Link to="/initiative/sponsor" className="block">
                    <Button
                      variant="outline"
                      className="w-full border-white/20 text-white bg-transparent hover:bg-white/10 gap-2"
                    >
                      <Heart className="h-4 w-4" />
                      {t('كن راعياً', 'Become a sponsor')}
                    </Button>
                  </Link>
                  <button
                    onClick={toggleLanguage}
                    className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white/85 hover:bg-white/10 transition-colors text-sm"
                  >
                    <Globe className="h-4 w-4" />
                    {i18n.language === 'ar' ? 'English' : 'العربية'}
                  </button>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 space-y-3 text-[11px] text-white/60">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-3 w-3 text-[#FFA92C]" />
                    <img src={logo} alt="" className="h-4 w-auto" />
                    <span className="text-white/70">×</span>
                    <span>{t('نادي الجلاء', 'Al-Jalaa')}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-5">
                    <span className="text-white/40">{t('الداعم التقني:', 'Technical Supporter:')}</span>
                    <img
                      src="/logos/champions-hub-horizontal-dark.png"
                      alt="Champions Hub"
                      className="h-3.5 w-auto opacity-85"
                    />
                  </div>
                  <Link to="/" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white pt-2">
                    <ArrowIcon className="h-3 w-3" />
                    {t('العودة للموقع الرئيسي', 'Back to main site')}
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default InitiativeHeader;
